'use client';

import { useState, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import LazyLiquidEther from '@/components/backgrounds/LazyLiquidEther';
import FlowingMenu, { MenuItemData } from '@/components/ui/FlowingMenu';
import ProjectModal, { ProjectData } from '@/components/ui/ProjectModal';
import LazyMapEmbed from '@/components/ui/LazyMapEmbed';

/**
 * About Us Page
 * =============
 *
 * Comprehensive about page for Magnate Group showcasing:
 * - Company vision and mission
 * - Key achievements (20+ years, 10+ projects, 3000+ families)
 * - Why choose Magnate Group
 * - Project portfolio overview
 * - Quality standards and sustainability commitment
 */

// Stats data
const companyStats = [
  { value: '20+', label: 'Years of Excellence' },
  { value: '10+', label: 'Premium Projects' },
  { value: '3000+', label: 'Happy Families' },
];

// Why choose us features
const whyChooseUs = [
  {
    icon: '📍',
    title: 'Prime Locations',
    description: 'Strategic sites across Ahmedabad\'s key growth corridors and emerging neighborhoods.',
  },
  {
    icon: '🏗️',
    title: 'Smart Architecture',
    description: 'Natural light optimization, cross-ventilation design, and sky gardens in every project.',
  },
  {
    icon: '⚡',
    title: 'Future-Ready',
    description: 'EV charging infrastructure, smart access systems, and co-working spaces built-in.',
  },
  {
    icon: '✅',
    title: 'Transparent Process',
    description: 'Real-time progress updates, branded material sourcing, and RERA compliant projects.',
  },
];

// Core values
const coreValues = [
  'Innovative Design',
  'Premium Materials',
  'Sustainable Practices',
  'Award-Winning Architecture',
];

// Quality features
const qualityFeatures = [
  'Premium vitrified tiles flooring',
  'Granite countertops with stainless steel sinks',
  'Branded sanitary ware and electrical fittings',
  'Laminated flush doors with quality hardware',
  'Premium aluminum windows with glazing',
  'Earthquake-resistant RCC frame construction',
];

// Our Projects data for FlowingMenu (excluding Regalia as this is the Regalia website)
const ourProjects: MenuItemData[] = [
  {
    link: '#',
    text: 'Magnate Luxuria',
    image: '/assets/images/other_projects/MAGNATE_LUXURIA_Front_view.webp',
  },
  {
    link: '#',
    text: 'Magnate Legend',
    image: '/assets/images/other_projects/Magnate%20Legend%20Front_Night_View.webp',
  },
  {
    link: '#',
    text: 'Magnate Lifestyle',
    image: '/assets/images/other_projects/MAGNATE_LIFESTYLE_01.webp',
  },
  {
    link: '#',
    text: 'Magnate Square',
    image: '/assets/images/other_projects/MAGNATE_SQUARE_Master%20Cam_004.webp',
  },
  {
    link: '#',
    text: 'Magnate Impression',
    image: '/assets/images/other_projects/Magnate_impression_FRONT_ALIGN.webp',
  },
  {
    link: '#',
    text: 'Magnate Lavish',
    image: '/assets/images/other_projects/05_MAGNET%20Lavish_ALIGN.webp',
  },
];

// Detailed project data for modal display
const projectDetails: Record<string, ProjectData> = {
  'Magnate Luxuria': {
    name: 'Magnate Luxuria',
    tagline: 'Lush. Lustrous. Lucid.',
    type: 'Residential & Commercial',
    configurations: ['3 BHK'],
    location: 'Nr. Vishnudhara Garden, Opp. Seventh Parisar, Gota (Jagatpur), Ahmedabad',
    description: 'Magnate Luxuria brings together the essence of luxurious living with modern convenience. Located near the iconic Jaguar Showroom and Vishnudhara Gardens, this project offers spacious 3 BHK apartments with premium finishes, earthquake-resistant construction, and thoughtfully designed amenities for an elevated lifestyle.',
    image: '/assets/images/other_projects/MAGNATE_LUXURIA_Front_view.webp',
    amenities: ['Designed Foyer', 'Landscape Garden', 'Senior Citizen Area', 'Yoga Deck', 'Gym', 'Children Play Area', 'Club House', 'Fire Safety', 'CCTV Camera'],
  },
  'Magnate Legend': {
    name: 'Magnate Legend',
    tagline: 'Upscale Living - Personifies Magnificent Luxury',
    type: 'Residential',
    configurations: ['3 BHK'],
    location: 'Sardar Dham to Umiyadham Road, Nr. Vaishnodevi Circle, Khodiyar, Ahmedabad',
    description: 'Magnate Legend represents the pinnacle of upscale living near the sacred Vaishnodevi Circle. With 12 floors of premium 3 BHK residences, this project offers proximity to top educational institutions like Nirma University, healthcare facilities like KD Hospital, and excellent connectivity via S.G. Highway and S.P. Ring Road.',
    image: '/assets/images/other_projects/Magnate%20Legend%20Front_Night_View.webp',
    amenities: ['Multipurpose Court', 'Landscape Garden', 'Senior Citizen Area', 'Children Play Area', 'Jogging Track', 'Club House', 'Gym', 'Yoga Deck', 'Fire Safety', 'CCTV Camera', '24 Hrs Security'],
  },
  'Magnate Lifestyle': {
    name: 'Magnate Lifestyle',
    tagline: 'Where Happiness Meets Serenity',
    type: 'Residential & Commercial',
    configurations: ['2 BHK', '3 BHK'],
    location: 'Opp. Tulip-1, Behind Gorbandh Hotel, Vaishno Devi Circle, S G Highway, Ahmedabad',
    description: 'Magnate Lifestyle perfectly blends residential comfort with commercial opportunity. Located on the prestigious S.G. Highway near Vaishno Devi Circle, it offers well-designed 2 BHK and 3 BHK apartments with modern amenities, excellent connectivity to Adani Shantigram, Nirma University, and major corporate hubs.',
    image: '/assets/images/other_projects/MAGNATE_LIFESTYLE_01.webp',
    amenities: ['Multipurpose Room', 'Yoga Deck', 'Club House (G+1)', 'Children\'s Play Area', 'Party Lawn', 'Gymnasium', 'Senior Citizen Area', '24 Hrs Water & Security', 'CCTV Camera', 'Jogging Track', 'DG Backup System'],
  },
  'Magnate Square': {
    name: 'Magnate Square',
    tagline: 'Be Smart Let\'s Start - Attract Success Like Never Before',
    type: 'Commercial',
    configurations: ['Showrooms', 'Offices', 'Banks'],
    location: 'Opp. Home Town-3, 100 FT Main Road, New Ranip, Ahmedabad',
    description: 'Magnate Square is a premium commercial destination designed for success. This Ground + 6 storey building on a 100 FT wide main road offers prime retail showrooms, modern office spaces, and facilities for restaurants, banks, and educational institutions. Ideal for businesses seeking visibility and growth.',
    image: '/assets/images/other_projects/MAGNATE_SQUARE_Master%20Cam_004.webp',
    amenities: ['2 Level Basement Parking', 'Exclusive Elevators', '24x7 CCTV Surveillance', 'Fire Safety', 'Earthquake Resistant Design', 'D.G. Set Backup', 'Elegant Entrance Foyer'],
  },
  'Magnate Impression': {
    name: 'Magnate Impression',
    tagline: 'Impression of Life That Lasts Forever',
    type: 'Residential',
    configurations: ['3 BHK'],
    location: 'Bhadaj Circle to Shilaj Circle Ring Road, Behind Science City, Ahmedabad',
    description: 'Magnate Impression creates lasting impressions with its thoughtfully designed 3 BHK residences behind the iconic Science City. Offering 7 floors of premium living, each unit features spacious rooms, multiple balconies, and proximity to major hospitals like CIMS and Zydus, shopping destinations, and excellent educational institutions.',
    image: '/assets/images/other_projects/Magnate_impression_FRONT_ALIGN.webp',
    amenities: ['Children Play Area', 'Banquet', 'Indoor Games', 'Gym', 'Yoga Deck', '2x Allotted Car Parking', 'CCTV Surveillance', 'Fire Safety', 'Swing Plaza', 'Security Cabin'],
  },
  'Magnate Lavish': {
    name: 'Magnate Lavish',
    tagline: 'Lavish. Leisure. Lucrative.',
    type: 'Residential',
    configurations: ['2 BHK', '3 BHK'],
    location: 'Sardardham to Umiyadham Road, Nr. Vaishnodevi Circle, Khodiyar, Ahmedabad',
    description: 'Magnate Lavish lives up to its name with generous living spaces and an abundance of amenities. Located near the spiritual Vaishnodevi Circle, this 14-floor project offers 2 BHK and 3 BHK apartments with unique features like an Oxygen Park, Lavish Party Lawn, and Mother\'s Hangout. Perfect for families seeking a balanced lifestyle.',
    image: '/assets/images/other_projects/05_MAGNET%20Lavish_ALIGN.webp',
    amenities: ['Multipurpose Court', 'Landscape Garden', 'Senior Citizen Area', 'Gym', 'Yoga Deck', 'Club House', 'Children Play Area', 'Jogging Track', 'Oxygen Park', 'Lavish Party Lawn', 'Mother\'s Hang Out', 'Toddler\'s Play Area', 'Central Plaza', 'Bird Feeder', 'Fountain'],
  },
};

export default function AboutPage() {
  // Modal state management
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Handle project item click from FlowingMenu
   * Opens the modal with the selected project's details
   */
  const handleProjectClick = useCallback((item: MenuItemData) => {
    const projectData = projectDetails[item.text];
    if (projectData) {
      setSelectedProject(projectData);
      setIsModalOpen(true);
    }
  }, []);

  /**
   * Close the project modal
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Delay clearing project data for smooth exit animation
    setTimeout(() => setSelectedProject(null), 300);
  }, []);

  return (
    <main className="min-h-screen relative overflow-hidden">
      {/* Liquid Ether Background */}
      <div className="fixed inset-0 z-0">
        <LazyLiquidEther
          colors={['#D4A574', '#8B6914', '#C9A227']}
          mouseForce={20}
          cursorSize={100}
          resolution={0.5}
          isViscous={false}
          viscous={30}
          autoDemo={true}
          autoSpeed={0.5}
          autoIntensity={2.2}
          autoResumeDelay={2000}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Content wrapper - above background */}
      <div className="relative z-10">
        {/* Magnate Group Logo Section */}
        <section className="pt-[108px] md:pt-32 pb-8 px-4">
          <div className="max-w-6xl mx-auto flex justify-center">
            <div className="logo-golden-shine inline-block" style={{ lineHeight: 0, fontSize: 0 }}>
              <Image
                src="/assets/LOGOS/magnate_logo.svg"
                alt="Magnate Group Logo"
                width={752}
                height={242}
                className="golden-logo"
                style={{ display: 'block', width: '40vw', maxWidth: '538px', height: 'auto' }}
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <section className="pb-16 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-amber-400 font-medium tracking-wider mb-4">ABOUT MAGNATE GROUP</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            We Build Tomorrow&apos;s Ahmedabad
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Premium residences where smart living meets timeless investment value.
            Across Ahmedabad&apos;s growing landscape, we create high-rise homes where technology,
            sustainability, and refined aesthetics converge.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
            {companyStats.map((stat) => (
              <div
                key={stat.label}
                className="glass-card text-center p-4 md:p-6 rounded-3xl"
              >
                <div className="text-3xl md:text-5xl font-bold text-amber-400 mb-2">{stat.value}</div>
                <div className="text-white/90 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision */}
            <div className="glass-card p-8 rounded-3xl">
              <div className="w-12 h-12 bg-amber-400/30 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🎯</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-white/90 leading-relaxed">
                &quot;To reshape Ahmedabad&apos;s skyline with landmark addresses that inspire pride today
                and remain investment-worthy tomorrow.&quot;
              </p>
            </div>

            {/* Mission */}
            <div className="glass-card p-8 rounded-3xl">
              <div className="w-12 h-12 bg-amber-400/30 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🚀</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-white/90 leading-relaxed">
                Every square foot carries our promise: future-ready residences that appreciate in value
                while elevating daily life through exceptional quality and uncompromising attention to detail.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose Magnate Group</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              We deliver exceptional quality in every project, ensuring customer satisfaction through
              transparent dealings and timely delivery.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item) => (
              <div
                key={item.title}
                className="glass-card p-6 rounded-2xl"
              >
                <div className="w-14 h-14 bg-amber-400/30 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-2xl">{item.icon}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">Our Core Values</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {coreValues.map((value) => (
              <div
                key={value}
                className="px-6 py-3 bg-amber-400/10 border border-amber-400/30 rounded-full text-amber-400 font-medium"
              >
                {value}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Magnate Regalia */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-amber-400 font-medium tracking-wider mb-4">FLAGSHIP PROJECT</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Magnate Regalia</h2>
              <p className="text-white/70 leading-relaxed mb-6">
                Located at Science Park, Behind Science City, Magnate Regalia stands as a testament
                to our commitment to excellence. Offering premium 4BHK and 5BHK apartments spread
                across 20+ floors, this project features ultra-modern amenities designed for the
                discerning homeowner.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white/70">
                  <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                  Premium 4BHK & 5BHK configurations
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                  20+ floors with panoramic views
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                  World-class amenities including swimming pool, theater, gym
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                  EV charging infrastructure
                </li>
              </ul>
              <Link
                href="/amenities"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors"
              >
                Explore Amenities
                <span>→</span>
              </Link>
            </div>
            <div className="relative aspect-[4/3] bg-white/10 rounded-3xl overflow-hidden">
              <Image
                src="/assets/images/Cam_15.webp"
                alt="Magnate Regalia Building"
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Projects Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-amber-400 font-medium tracking-wider mb-4">PORTFOLIO</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Projects</h2>
            <p className="text-white/60 max-w-2xl mx-auto mb-3">
              Explore our portfolio of premium residential developments across Ahmedabad.
            </p>
            <p className="text-white/60 text-sm flex items-center justify-center gap-2">
              <span className="md:hidden">Tap</span>
              <span className="hidden md:inline">Click</span>
              on a project to view details
              <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </p>
          </div>
          <div className="glass-card rounded-3xl overflow-hidden h-[280px] sm:h-[350px] md:h-[500px]">
            <FlowingMenu items={ourProjects} onItemClick={handleProjectClick} />
          </div>
          <div className="text-center mt-8">
            <a
              href="https://www.magnategroup.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors"
            >
              Visit magnategroup.in
              <span>→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Project Locations Map */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-amber-400 font-medium tracking-wider mb-4">LOCATIONS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Project Locations</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Discover our premium developments across Ahmedabad&apos;s most sought-after areas and emerging growth zones.
            </p>
          </div>
          <div className="glass-card rounded-3xl overflow-hidden">
            <div className="relative w-full aspect-video md:aspect-[21/9]">
              {/*
                Click-to-load facade: the Google Maps iframe (and its ~35
                third-party cookies) is only mounted after the user clicks.
                See LazyMapEmbed for the reasoning.
              */}
              <LazyMapEmbed
                src="https://www.google.com/maps/d/embed?mid=1ZOCN-TI8cGCUam9Av4-b3LMFqBOi4AA&ehbc=2E312F&iwloc=&output=embed&z=11"
                title="Magnate Group Project Locations in Ahmedabad"
                label="Our project locations across Ahmedabad"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Quality Standards</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              We use only the finest materials and construction practices to ensure your home
              stands the test of time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {qualityFeatures.map((feature) => (
              <div
                key={feature}
                className="glass-card flex items-center gap-3 p-4 rounded-xl"
              >
                <span className="text-amber-400 text-lg">✓</span>
                <span className="text-white/90">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-amber-400/20 to-amber-600/20 rounded-3xl border border-amber-400/30 p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Invest in Your Future?
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">
              Discover premium residences that appreciate with Ahmedabad&apos;s growth.
              Schedule a site visit today and experience the Magnate difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="px-8 py-4 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors"
              >
                Schedule a Visit
              </Link>
              <a
                href="tel:+919664805202"
                className="px-8 py-4 bg-white/10 text-white font-semibold rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
              >
                Call +91 96648 05202
              </a>
            </div>
          </div>
        </div>
      </section>

        {/* Company Info Footer */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/50 text-sm mb-2">Developer</p>
            <p className="text-white/80 font-medium mb-4">Motherland Infrastructure</p>
            <p className="text-white/50 text-sm">
              All projects are RERA registered with the Gujarat Real Estate Regulatory Authority
            </p>
          </div>
        </section>
      </div>

      {/* Project Details Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </main>
  );
}
